import {AfterContentInit, ChangeDetectionStrategy, Component, DOCUMENT, inject, input, InputSignal, NgZone, OnDestroy, Renderer2, RendererFactory2} from '@angular/core';
import {TutorialOptions} from '../../models/tutorial/tutorial-options';
import {Position} from '../../models/position.model';
import {combineLatest} from 'rxjs';
import {debounceTime, filter} from 'rxjs/operators';
import {PlacementType} from '../../types/placement-type.type';
import {TutorialService} from '../../services/tutorial/tutorial.service';
import {Event, NavigationEnd, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {TranslationKey} from '../../types/available-translations.type';
import {AppQuery} from '../../queries/app.query';
import {DocumentQuery} from '../../queries/document.query';
import {AuthQuery} from '../../queries/auth.query';
import {Step} from '../../models/tutorial/step';
import {BasicSubscribableComponent} from '../dummy-components/basic-subscribable-component';
import {environment} from '../../../environments/environment';
import {TutorialStepOffset} from '../../models/tutorial/tutorial-step-offset';
import {TutorialQuery} from '../../queries/tutorial.query';
import {SnackbarService} from '../../services/snackbar/snackbar.service';
import {ClipboardService} from '../../services/clipboard/clipboard.service';

@Component({
    selector: 'app-tutorial',
    changeDetection: ChangeDetectionStrategy.Eager,
    template: ``
})
export class TutorialComponent extends BasicSubscribableComponent implements AfterContentInit, OnDestroy {
    public tutorialOptions: InputSignal<TutorialOptions> = input<TutorialOptions>({
        backdropColor: '',
        steps: []
    });
    private readonly offset: number;
    private readonly isProduction: boolean;
    private currentStepIndex = 0;
    private readonly renderer: Renderer2;
    private filteredOptions: TutorialOptions;
    private skippedSteps: Array<Step>;
    private stepElement: HTMLElement | undefined;
    private lastStepBounds: DOMRect | undefined;
    private layoutWatchFrameId: number | undefined;
    private readonly boundEventListenerCallback: () => Promise<void>;
    private readonly document = inject<Document>(DOCUMENT);
    private readonly rendererFactory = inject(RendererFactory2);
    private readonly tutorialService = inject(TutorialService);
    private readonly translateService = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly appQuery = inject(AppQuery);
    private readonly authQuery = inject(AuthQuery);
    private readonly documentQuery = inject(DocumentQuery);
    private readonly ngZone = inject(NgZone);
    private readonly tutorialQuery = inject(TutorialQuery);
    private readonly snackbarService = inject(SnackbarService);
    private readonly clipboardService = inject(ClipboardService);

    public constructor() {
        super();
        this.renderer = this.rendererFactory.createRenderer(null, null);
        this.filteredOptions = {
            backdropColor: '',
            steps: []
        };
        this.offset = 24;
        this.skippedSteps = [];
        this.isProduction = environment.production;
        this.boundEventListenerCallback = this.eventListenerCallback.bind(this);
        this.subscriptions.add(this.tutorialService.getTutorialStartTriggerEvent()
            .subscribe(() => {
                this.startTutorial()
                    .then();
            }));
    }

    public ngAfterContentInit(): void {
        this.subscriptions.add(this.router.events.pipe(filter((routerEvent: Event): routerEvent is NavigationEnd => routerEvent instanceof NavigationEnd))
            .subscribe(async () => {
                this.skippedSteps = [];
                await this.startTutorial();
            }));

        this.subscriptions.add(combineLatest([
            this.appQuery.showSearchOverlay$,
            this.documentQuery.loading$
        ])
            .pipe(debounceTime(100))
            .subscribe(async ([showSearch, isDocumentLoading]) => {
                if (showSearch || !isDocumentLoading) {
                    await this.startTutorial();
                }
            }));
    }

    public override ngOnDestroy(): void {
        this.stopWatchingStepLayout();
        super.ngOnDestroy();
    }

    public async eventListenerCallback(): Promise<void> {
        await this.nextStep(1);
    }

    public async startTutorial(): Promise<void> {
        if (!this.authQuery.getIsLoggedIn()) {
            return;
        }
        // set timeout to wait for DOM being loaded completely
        await this.ngZone.run(async () => {
            await this.createFilteredOptions();
            const { steps } = this.filteredOptions;

            if (steps.length > 0) {
                const backdropElement = this.createBackdropElement();
                if (backdropElement) {
                    this.renderer.appendChild(this.document.body, backdropElement);
                }
                this.setupEventListeners(backdropElement);
                await this.createStep();
            }
        });
    }

    public async createFilteredOptions(): Promise<void> {
        await this.tutorialService.fetchReadTutorialStepIds();
        const readStepIds = this.tutorialQuery.getReadTutorialStepIds();

        this.filteredOptions = {
            ...this.tutorialOptions(),
            steps: this.tutorialOptions()
                .steps
                .filter(step => this.skippedSteps.filter(skippedStep => skippedStep.element === step.element).length === 0 && this.getElement(step.element) !==
                    null && !readStepIds.includes(step.element))
        };
        this.currentStepIndex = 0;
    }

    public createBackdropElement(): HTMLElement | null {
        const hasBackdropElement = this.getElementById('tutorial-helper-backdrop');
        if (hasBackdropElement) {
            return null;
        }
        const backdropElement = this.renderer.createElement('div');
        this.renderer.setAttribute(backdropElement, 'id', 'tutorial-helper-backdrop');
        this.renderer.setAttribute(backdropElement, 'data-qa', 'tutorial-helper-backdrop');
        this.renderer.addClass(backdropElement, 'tutorial-helper-backdrop');
        return backdropElement;
    }

    public createActiveElement(backdrop: Element | null, elementBounds: DOMRect): HTMLElement {
        const { backdropColor } = this.filteredOptions;
        let activeElement = this.getElement('#tutorial-helper-backdrop .tutorial-helper-active') as HTMLElement;

        if (!activeElement) {
            activeElement = this.renderer.createElement('div');
            this.renderer.setAttribute(activeElement, 'id', 'tutorial-helper-active');
            this.renderer.addClass(activeElement, 'tutorial-helper-active');
            this.renderer.appendChild(backdrop, activeElement);
        }
        this.renderer.setStyle(activeElement, 'top', Math.round(elementBounds.top) + 'px');
        this.renderer.setStyle(activeElement, 'left', Math.round(elementBounds.left) + 'px');
        this.renderer.setStyle(activeElement, 'height', elementBounds.height + 'px');
        this.renderer.setStyle(activeElement, 'width', elementBounds.width + 'px');
        this.renderer.setStyle(activeElement, 'borderRadius', '32px');
        this.renderer.setStyle(activeElement, 'boxShadow', `0 0 0 9999px ${backdropColor}`);

        return activeElement;
    };

    public createDescriptionElement(backdrop: Element | null, currentStep: Step): HTMLElement {
        let descriptionElement = this.getElement('#tutorial-helper-backdrop .tutorial-helper-active-description') as HTMLElement;
        let descriptionContent = '';
        const titleKey: TranslationKey = currentStep.titleKey;
        const descriptionKey: TranslationKey = currentStep.descriptionKey;

        if (!descriptionElement) {
            descriptionElement = this.renderer.createElement('div');
            this.renderer.setStyle(descriptionElement, 'willChange', 'transform');
            this.renderer.addClass(descriptionElement, 'tutorial-helper-active-description');

            descriptionContent += `<div id='tutorial-helper-active-description-title'></div>`;
            descriptionContent += `<div id='tutorial-helper-active-description-text'></div>`;

            this.renderer.setProperty(descriptionElement, 'innerHTML', descriptionContent);
            this.renderer.setStyle(descriptionElement, 'visibility', 'hidden');
            this.renderer.appendChild(backdrop, descriptionElement);
        }

        descriptionElement.dataset.qa = 'tutorial-helper-' + titleKey.toLowerCase()
            .replace('.title', '')
            .replace('tutorial.', '');

        const descriptionTitle = this.getElementById('tutorial-helper-active-description-title');
        if (descriptionTitle) {
            const title = this.translateService.instant(titleKey);
            this.renderer.setProperty(descriptionTitle, 'innerHTML', title);
        }

        const descriptionText = this.getElementById('tutorial-helper-active-description-text');
        if (descriptionText) {
            const description = this.translateService.instant(descriptionKey);
            this.renderer.setProperty(descriptionText, 'innerHTML', description);
        }

        if (!this.isProduction) {
            descriptionElement.dataset.showKey = '0';
            descriptionElement.dataset.titleKey = titleKey;
            descriptionElement.dataset.descriptionKey = descriptionKey;
            descriptionElement.addEventListener('click', (mouseEvent: MouseEvent) => {
                const currentTitleKey: TranslationKey = descriptionElement.dataset.titleKey as TranslationKey;
                const currentDescriptionKey: TranslationKey = descriptionElement.dataset.descriptionKey as TranslationKey;
                mouseEvent.preventDefault();
                mouseEvent.stopImmediatePropagation();
                const currentElement = this.getElement(currentStep.element);
                if (currentElement) {
                    const isShowingKey = descriptionElement.dataset.showKey === '1';
                    if (!isShowingKey) {
                        descriptionElement.dataset.showKey = '1';
                        const description = currentTitleKey + '<br>' + currentDescriptionKey + '<br>' + currentStep.element;
                        this.renderer.setProperty(descriptionText, 'innerHTML', description);
                        this.clipboardService.copyToClipboard(currentTitleKey)
                            .then(() => {
                                this.snackbarService.showSuccess('COPIED_TEXT_TO_CLIPBOARD', {
                                    text: currentTitleKey
                                });
                            });
                    } else {
                        descriptionElement.dataset.showKey = '0';
                        const description = this.translateService.instant(currentDescriptionKey);
                        this.renderer.setProperty(descriptionText, 'innerHTML', description);
                    }
                    this.setPositionAndSize()
                        .then();
                }
            });

        }

        return descriptionElement;
    };

    public createArrowElement(backdrop: Element | null): HTMLElement {
        let arrowElement = this.getElement('#tutorial-helper-backdrop #tutorial-helper-arrow') as HTMLElement;

        if (!arrowElement) {
            arrowElement = this.renderer.createElement('div');
            this.renderer.setAttribute(arrowElement, 'id', 'tutorial-helper-arrow');
            this.renderer.appendChild(backdrop, arrowElement);
        }
        return arrowElement;
    };

    public async createStep(): Promise<void> {
        const { steps } = this.filteredOptions;
        const currentStep = steps[this.currentStepIndex];
        const element = currentStep.element;

        const currentElement: HTMLElement | null = await this.getCheckedElement(element);
        if (!currentElement) {
            if (steps.length - 1 > 0) {
                this.skippedSteps.push(currentStep);
                this.nextStep(1, false)
                    .then();
            } else {
                await this.closeTutorial();
            }
            return;
        }

        if (currentElement && !await this.elementVisible(currentElement)) {
            this.skippedSteps.push(currentStep);
            this.nextStep(1, false)
                .then();
            return;
        }

        this.renderer.addClass(this.document.body, 'overflow-scroll-behavior');
        const scrollIntoViewOptions: ScrollIntoViewOptions = {
            behavior: 'smooth',
            block: 'center'
        };
        currentElement.scrollIntoView(scrollIntoViewOptions);

        const elementBounds = currentElement.getBoundingClientRect();
        const backdropElement = this.getElementById('tutorial-helper-backdrop');
        this.createActiveElement(backdropElement, elementBounds);
        this.createDescriptionElement(backdropElement, currentStep);
        this.createArrowElement(backdropElement);
        await this.setPositionAndSize();
        this.watchStepLayout(currentElement);
    }

    public async setPositionAndSize(): Promise<void> {
        const { steps } = this.filteredOptions;
        const currentStep = steps[this.currentStepIndex];
        const currentElement: HTMLElement | null = currentStep?.element ? await this.getCheckedElement(currentStep.element) : null;
        if (currentElement) {
            const descriptionElement = this.getElement('#tutorial-helper-backdrop .tutorial-helper-active-description') as HTMLElement;

            if (!descriptionElement) {
                await this.closeTutorial();
                return;
            }

            const activeElement = this.getElement('#tutorial-helper-backdrop .tutorial-helper-active') as HTMLElement;
            const arrowElement = this.getElement('#tutorial-helper-backdrop #tutorial-helper-arrow') as HTMLElement;

            const descriptionArea = descriptionElement.getBoundingClientRect();
            const position = this.calculatePositions(currentElement, descriptionElement, currentStep.position, currentStep.offset, currentStep.placement);
            const arrowPosition = this.calculateArrowPosition(arrowElement, currentStep.position, position, activeElement, descriptionElement);

            if (position.x + descriptionArea.width >= window.innerWidth) {
                const differenceX = position.x + descriptionArea.width - window.innerWidth;
                position.x = position.x - differenceX;
            } else if (position.x < 0) {
                position.x = 0;
                if (descriptionArea.width >= window.innerWidth) {
                    this.renderer.setStyle(descriptionElement, 'width', window.innerWidth - (position.x * 2) + 'px');
                }
            }
            this.renderer.setStyle(descriptionElement, 'transform', 'translate3d(' + position.x + 'px, ' + position.y + 'px, 0)');
            this.renderer.setStyle(descriptionElement, 'visibility', 'visible');
            this.renderer.setStyle(arrowElement, 'transform', 'translate3d(' + arrowPosition.x + 'px, ' + arrowPosition.y + 'px, 0)');
        }
    }

    public async repositionCurrentStep(): Promise<void> {
        const { steps } = this.filteredOptions;
        const currentStep = steps[this.currentStepIndex];
        if (!currentStep) {
            return;
        }
        const currentElement: HTMLElement | null = await this.getCheckedElement(currentStep.element);
        if (!currentElement) {
            return;
        }
        const elementBounds = currentElement.getBoundingClientRect();
        const backdropElement = this.getElementById('tutorial-helper-backdrop');
        this.createActiveElement(backdropElement, elementBounds);
        await this.setPositionAndSize();
    }

    public async closeTutorial(): Promise<void> {
        this.stopWatchingStepLayout();
        this.renderer.removeClass(this.document.body, 'overflow-scroll-behavior');

        const backdropElement = this.getElementById('tutorial-helper-backdrop') as HTMLElement;
        if (backdropElement) {
            backdropElement.removeEventListener('click', this.boundEventListenerCallback);
            this.renderer.removeChild(this.document.body, backdropElement);
        }
        this.document.body.removeEventListener('keydown', this.boundEventListenerCallback);

        const { onComplete } = this.filteredOptions;
        if (onComplete) {
            onComplete();
        }
        this.currentStepIndex = 0;
        await this.tutorialService.saveCompleteProgressPermanently();
    };

    public async nextStep(increment: number, saveProgress: boolean = true): Promise<void> {
        this.stopWatchingStepLayout();
        await this.createFilteredOptions();

        const { steps } = this.filteredOptions;
        if (this.currentStepIndex in steps) {
            const currentStep = steps[this.currentStepIndex].element;

            if (saveProgress) {
                this.tutorialService.saveCurrentProgressToStore(currentStep);
            }
            this.currentStepIndex = this.currentStepIndex + increment;
            if (this.currentStepIndex >= 0 && this.currentStepIndex <= steps.length - 1) {
                await this.createStep();
            } else {
                const activeElement = this.getElement(currentStep);
                this.renderer.removeClass(activeElement, 'tutorial-helper-active-element');
                await this.closeTutorial();
            }
        } else if (steps.length === 0) {
            await this.closeTutorial();
        }
    };

    public setupEventListeners(backdropElement: HTMLElement | null): void {
        if (backdropElement) {
            backdropElement.addEventListener('click', this.boundEventListenerCallback);
        }
        this.document.body.addEventListener('keydown', this.boundEventListenerCallback);
    };

    public getElement(selector: string): HTMLElement | null {
        return this.document.querySelector(selector);
    }

    public async getCheckedElement(selector: string): Promise<HTMLElement | null> {
        const elements = Array.from(this.document.querySelectorAll<HTMLElement>(selector));
        for (const element of elements) {
            if (await this.elementVisible(element)) {
                return element;
            }
        }
        return null;
    }

    public getElementById(id: string): HTMLElement | null {
        return this.document.getElementById(id);
    }

    public elementVisible(element: HTMLElement): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            const observer = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting) {
                    const boundingBox = element.getBoundingClientRect();
                    const positionX = boundingBox.x + Math.round(boundingBox.width / 2);
                    const positionY = boundingBox.y + Math.round(boundingBox.height / 2);
                    const elementsOnPosition = document.elementsFromPoint(positionX, positionY);
                    for (const elementOnPosition of elementsOnPosition) {
                        // the searchbar overlays the element
                        if (elementOnPosition.tagName.toLowerCase() === 'app-search-dashboard') {
                            resolve(false);
                            return;
                        }
                        if (elementOnPosition === element) {
                            break;
                        }
                    }
                    resolve(true);
                    observer.disconnect();
                }
                resolve(false);
            });
            observer.observe(element);
        });
    }

    public calculateOffset(offset: TutorialStepOffset | undefined): Array<number> {
        let offsetX = 0;
        let offsetY = 0;
        if (offset) {
            if (offset.left) {
                offsetX += offset.left;
            }
            if (offset.right) {
                offsetX += -offset.right;
            }
            if (offset.top) {
                offsetY += offset.top;
            }
            if (offset.bottom) {
                offsetY += -offset.bottom;
            }
        }
        return [
            offsetX,
            offsetY
        ];
    }

    public calculatePositions(element: HTMLElement, description: Element, position: PlacementType, offset: TutorialStepOffset | undefined, placement: PlacementType | undefined): Position {
        const calculatedPosition: Position = {
            x: 0,
            y: 0
        };
        const elementBounds = element.getBoundingClientRect();
        const descriptionBounds = description.getBoundingClientRect();
        const [offsetX, offsetY] = this.calculateOffset(offset);

        const factor = descriptionBounds.width > elementBounds.width ? -1 : 1;
        const verticalX = Math.round(elementBounds.x + (factor * Math.abs(elementBounds.width - descriptionBounds.width) / 2));
        const verticalY = Math.round(elementBounds.y + (factor * Math.abs(elementBounds.height - descriptionBounds.height) / 2));

        switch (position) {
            case 'center':
                calculatedPosition.x = verticalX;
                calculatedPosition.y = verticalY;
                break;

            case 'right':
                calculatedPosition.x = Math.round(elementBounds.x + elementBounds.width + this.offset);
                calculatedPosition.y = Math.round(elementBounds.y + elementBounds.height / 2 - descriptionBounds.height / 2);
                break;

            case 'bottom':
                calculatedPosition.x = verticalX;
                calculatedPosition.y = Math.round(elementBounds.y + elementBounds.height + this.offset);
                break;

            case 'left':
                calculatedPosition.x = Math.round(elementBounds.x - descriptionBounds.width - this.offset);
                calculatedPosition.y = Math.round(elementBounds.y + elementBounds.height / 2 - descriptionBounds.height / 2);
                break;

            case 'top':
            default:
                calculatedPosition.x = verticalX;
                calculatedPosition.y = Math.round(elementBounds.y - descriptionBounds.height - this.offset);
                break;
        }

        if (placement === 'bottom') {
            calculatedPosition.y += Math.round(descriptionBounds.height + this.offset);
        }

        calculatedPosition.x += offsetX;
        calculatedPosition.y += offsetY;

        return calculatedPosition;
    };

    public calculateArrowPosition(element: HTMLElement, placement: PlacementType, descriptionPosition: Position, active: HTMLElement, description: HTMLElement): Position {
        const offsetPixel = 1;
        const position: Position = {
            x: 0,
            y: 0
        };
        const activeBounds = active.getBoundingClientRect();
        const descriptionBounds = description.getBoundingClientRect();

        this.renderer.removeAttribute(element, 'class');
        this.renderer.addClass(element, 'tutorial-helper-arrow');

        switch (placement) {
            case 'top':
                this.renderer.addClass(element, 'tutorial-helper-arrow-down');
                position.x = Math.round(activeBounds.x + (activeBounds.width / 2) - 24);
                position.y = Math.round(descriptionPosition.y + descriptionBounds.height - offsetPixel);
                break;

            case 'right':
                this.renderer.addClass(element, 'tutorial-helper-arrow-left');
                position.x = Math.round(descriptionPosition.x - 24 + offsetPixel);
                position.y = Math.round(activeBounds.y + (activeBounds.height / 2) - 24);
                break;

            case 'left':
                this.renderer.addClass(element, 'tutorial-helper-arrow-right');
                position.x = Math.round(descriptionPosition.x + descriptionBounds.width - offsetPixel);
                position.y = Math.round(activeBounds.y + (activeBounds.height / 2) - 24);
                break;

            case 'center':
            case 'bottom':
            default:
                this.renderer.addClass(element, 'tutorial-helper-arrow-up');
                position.x = Math.round(activeBounds.x + (activeBounds.width / 2) - 24);
                position.y = Math.round(descriptionPosition.y - 24 + offsetPixel);
                break;
        }
        return position;
    };

    private watchStepLayout(element: HTMLElement): void {
        this.stopWatchingStepLayout();
        this.stepElement = element;
        this.lastStepBounds = element.getBoundingClientRect();
        this.layoutWatchFrameId = requestAnimationFrame(() => this.trackStepLayoutChanges());
    }

    private trackStepLayoutChanges(): void {
        const element = this.stepElement;
        if (!element) {
            return;
        }
        // A ResizeObserver would miss pure position shifts (e.g. sibling buttons rendered with a delay reflow the target
        // via justify-content without changing its size), so we compare the bounds every frame and only reposition on change.
        const bounds = element.getBoundingClientRect();
        if (this.hasStepBoundsChanged(bounds)) {
            this.lastStepBounds = bounds;
            this.repositionCurrentStep()
                .then();
        }
        this.layoutWatchFrameId = requestAnimationFrame(() => this.trackStepLayoutChanges());
    }

    private hasStepBoundsChanged(bounds: DOMRect): boolean {
        const lastBounds = this.lastStepBounds;
        return lastBounds?.top !== bounds.top
            || lastBounds.left !== bounds.left
            || lastBounds.width !== bounds.width
            || lastBounds.height !== bounds.height;
    }

    private stopWatchingStepLayout(): void {
        if (this.layoutWatchFrameId !== undefined) {
            cancelAnimationFrame(this.layoutWatchFrameId);
            this.layoutWatchFrameId = undefined;
        }
        this.stepElement = undefined;
        this.lastStepBounds = undefined;
    }
}
